// Reels-style UGC strip. Videos only play while visible, so a long strip does
// not decode a dozen streams at once.
class UgcVideos extends HTMLElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];
    if (!this.videos.length) return;

    this.header = this.closest('.ugc')?.querySelector('[data-ugc-arrows]');
    this.prevButton = this.closest('.ugc')?.querySelector('[data-ugc-prev]');
    this.nextButton = this.closest('.ugc')?.querySelector('[data-ugc-next]');

    this.videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting && intersectionRatio > 0.5) {
            // play() rejects on browsers that block autoplay; nothing to do about it.
            target.play?.().catch(() => {});
          } else {
            target.pause?.();
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    this.videos.forEach((video) => this.observer.observe(video));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.observer.disconnect();
      this.videos.forEach((video) => video.setAttribute('controls', ''));
    }

    this.onScroll = this.onScroll.bind(this);
    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);

    this.updateArrows();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  get step() {
    const first = this.querySelector('.ugc__item');
    if (!first) return 240;
    const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByPage(direction) {
    this.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateArrows();
    });
  }

  updateArrows() {
    if (!this.header) return;

    const { scrollLeft, scrollWidth, clientWidth } = this;
    const isScrollable = scrollWidth - clientWidth > 2;

    this.header.hidden = !isScrollable;
    if (!isScrollable) return;

    if (this.prevButton) this.prevButton.disabled = scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = scrollLeft >= scrollWidth - clientWidth - 2;
  }
}

if (!customElements.get('ugc-videos')) {
  customElements.define('ugc-videos', UgcVideos);
}
